/**
 * RKH API Service
 * Handles all API calls related to RKH (Rencana Kerja Harian)
 */

import api from '../../../auth/apiClient';
import { RKH_API_CONFIG } from '../apiConfig';
import {
    RKHListResponse,
    RKHDetailResponse,
    RKHFilters,
    RKHSummaryResponse,
    RKHSummaryFilters,
    AvailableBlocksResponse,
    LocationRegistrationRequest,
    LocationRegistrationResponse,
} from '../types';

class RKHApiService {
    /**
     * Fetch list of RKH with filters and pagination
     */
    async getRKHList(filters?: RKHFilters): Promise<RKHListResponse> {
        try {
            const params: any = {
                page: filters?.page || RKH_API_CONFIG.defaults.PAGE,
                limit: filters?.limit || RKH_API_CONFIG.defaults.LIMIT,
            };

            if (filters?.date_from) {
                params.date_from = filters.date_from;
            }
            if (filters?.date_to) {
                params.date_to = filters.date_to;
            }
            if (filters?.estate_id) {
                params.estate_id = filters.estate_id;
            }
            if (filters?.division_id) {
                params.division_id = filters.division_id;
            }
            if (filters?.stage) {
                params.stage = filters.stage;
            }

            const response = await api.get<RKHListResponse>(
                RKH_API_CONFIG.endpoints.LIST,
                { params }
            );

            return response.data;
        } catch (error: any) {
            console.error('Error fetching RKH list:', error);
            throw error;
        }
    }

    /**
     * Fetch detailed RKH data by ID
     */
    async getRKHDetail(id: string): Promise<RKHDetailResponse> {
        try {
            const response = await api.get<RKHDetailResponse>(
                RKH_API_CONFIG.endpoints.DETAIL(id)
            );

            return response.data;
        } catch (error: any) {
            console.error('Error fetching RKH detail:', error);
            throw error;
        }
    }

    /**
     * Fetch RKH summary data with estate/division/block hierarchy
     */
    async getRKHSummary(filters?: RKHSummaryFilters): Promise<RKHSummaryResponse> {
        try {
            const params: any = {};

            if (filters?.company_id) {
                params.company_id = filters.company_id;
            }
            if (filters?.date_from) {
                params.date_from = filters.date_from;
            }
            if (filters?.date_to) {
                params.date_to = filters.date_to;
            }
            if (filters?.estate_id) {
                params.estate_id = filters.estate_id;
            }
            if (filters?.division_id) {
                params.division_id = filters.division_id;
            }

            const response = await api.get<RKHSummaryResponse>(
                RKH_API_CONFIG.endpoints.SUMMARY,
                { params }
            );

            return response.data;
        } catch (error: any) {
            console.error('Error fetching RKH summary:', error);
            throw error;
        }
    }

    /**
     * Fetch available blocks for a specific RKH
     */
    async getAvailableBlocks(rkhId: string): Promise<AvailableBlocksResponse> {
        try {
            const response = await api.get<AvailableBlocksResponse>(
                RKH_API_CONFIG.endpoints.AVAILABLE_BLOCKS(rkhId)
            );

            return response.data;
        } catch (error: any) {
            console.error('Error fetching available blocks:', error);
            throw error;
        }
    }

    /**
     * Register locations for a specific RKH
     */
    async registerLocations(
        rkhId: string,
        request: LocationRegistrationRequest
    ): Promise<LocationRegistrationResponse> {
        try {
            const response = await api.post<LocationRegistrationResponse>(
                RKH_API_CONFIG.endpoints.REGISTER_LOCATIONS(rkhId),
                request
            );

            return response.data;
        } catch (error: any) {
            console.error('Error registering locations:', error);
            throw error;
        }
    }
}

export default new RKHApiService();
